///
/// EarthBrowser
///
/// Weather Grid Service
///
/// Manages downloading, decoding, and querying EBWX weather grid data.
/// Provides current conditions and forecasts for any lat/lon.
///
/// Created by Matt Giger on 4/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Constants } from "@/constants"
import { datasetUrl } from "@/utils/datasetUrl"
import { recordCrumb } from "@/earthbrowser/utils/CrashSentinel"
import { decodeEBWXHeader, decodeEBWXAtPoint, decodeEBWXPlane, type EBWXHeader, type EBWXPlane } from "./EBWXDecoder"
import {
	computeCurrentWeather,
	computeDailyForecasts,
	type CurrentWeather,
	type DailyForecast,
	type GridTimestepValues,
} from "./WeatherCompute"

const GRID_BASE_URL = `${Constants.baseStorageUrl}/datasets/weather_grid`
const CURRENT_GRID_URL = `${GRID_BASE_URL}/weather_current.bin.gz`
// Embed variant: surface variables only at 0.5 deg - a few MB instead of ~24
const CURRENT_LITE_GRID_URL = `${GRID_BASE_URL}/weather_current_lite.bin.gz`

let currentGridVariant: "full" | "lite" = "full"

/**
 * Select which current-conditions grid this page downloads. The embed sets
 * "lite" before any layer starts so city weather chips cost a few MB instead
 * of the full grid. Falls back to the full grid if the lite file is missing.
 */
export function setWeatherGridVariant(variant: "full" | "lite"): void {
	currentGridVariant = variant
}

const SYNOPTIC_GRID_URL = `${GRID_BASE_URL}/weather_synoptic.bin.gz`
const FRONTS_GRID_URL = `${GRID_BASE_URL}/weather_fronts.bin.gz`

/** Dev override for the fronts grid URL (serve a locally built grid) */
function frontsGridUrl(): string {
	try {
		const override = globalThis.localStorage?.getItem("earthbrowser_fronts_grid_url")
		if (override) return override
	} catch { }
	return FRONTS_GRID_URL
}

const JET_GRID_URL = `${GRID_BASE_URL}/weather_jet.bin.gz`

/** Dev override for the jet grid URL (serve a locally built grid) */
function jetGridUrl(): string {
	try {
		const override = globalThis.localStorage?.getItem("earthbrowser_jet_grid_url")
		if (override) return override
	} catch { }
	return JET_GRID_URL
}
const UPPER_GRID_URL = `${GRID_BASE_URL}/weather_upper.bin.gz`

/** Dev override for the upper (500 hPa) grid URL */
function upperGridUrl(): string {
	try {
		const override = globalThis.localStorage?.getItem("earthbrowser_upper_grid_url")
		if (override) return override
	} catch { }
	return UPPER_GRID_URL
}
const SHARD_SIZE_DEG = 10
const REFRESH_INTERVAL_MS = 30 * 60 * 1000
// Browsing forecasts around the map accumulates shards; keep only the most
// recently used few so the cache stays bounded on long sessions
const MAX_FORECAST_SHARDS = 6

export class WeatherGridService {
	private currentGrid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private synopticGrid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private frontsGrid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private jetGrid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private upperGrid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private forecastCache = new Map<string, { buffer: ArrayBuffer, header: EBWXHeader }>()
	private currentGridTimestamp = 0
	private synopticGridTimestamp = 0
	private frontsGridTimestamp = 0
	private jetGridTimestamp = 0
	private upperGridTimestamp = 0
	private loadingCurrent: Promise<void> | null = null
	private loadingSynoptic: Promise<void> | null = null
	private loadingFronts: Promise<void> | null = null
	private loadingJet: Promise<void> | null = null
	private loadingUpper: Promise<void> | null = null
	private loadingShards = new Map<string, Promise<void>>()

	async ensureCurrentGrid(): Promise<void> {
		const now = Date.now()
		if (this.currentGrid && (now - this.currentGridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loadingCurrent) {
			await this.loadingCurrent
			return
		}

		this.loadingCurrent = this._loadCurrentGrid()
		try {
			await this.loadingCurrent
		} finally {
			this.loadingCurrent = null
		}
	}

	private async _loadCurrentGrid(): Promise<void> {
		try {
			let response: Response | null = null
			if (currentGridVariant === "lite") {
				response = await fetch(datasetUrl(CURRENT_LITE_GRID_URL))
				if (!response.ok) {
					console.warn(`[WeatherGrid] Lite grid unavailable (${response.status}), falling back to full grid`)
					response = null
				}
			}
			if (!response) {
				response = await fetch(datasetUrl(CURRENT_GRID_URL))
			}
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch current grid: ${response.status}`)
				return
			}

			recordCrumb("grid:current decompress")
			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.currentGrid = { buffer: decompressed, header }
			this.currentGridTimestamp = Date.now()

			recordCrumb(`grid:current loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Current grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[WeatherGrid] Error loading current grid:', err)
		}
	}

	async ensureSynopticGrid(): Promise<void> {
		const now = Date.now()
		// Fresh by timestamp even when the buffer was released after compute:
		// don't refetch until the refresh interval elapses
		if ((now - this.synopticGridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loadingSynoptic) {
			await this.loadingSynoptic
			return
		}

		this.loadingSynoptic = this._loadSynopticGrid()
		try {
			await this.loadingSynoptic
		} finally {
			this.loadingSynoptic = null
		}
	}

	private async _loadSynopticGrid(): Promise<void> {
		try {
			const response = await fetch(datasetUrl(SYNOPTIC_GRID_URL))
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch synoptic grid: ${response.status}`)
				return
			}

			recordCrumb("grid:synoptic decompress")
			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.synopticGrid = { buffer: decompressed, header }
			this.synopticGridTimestamp = Date.now()

			recordCrumb(`grid:synoptic loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Synoptic grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[WeatherGrid] Error loading synoptic grid:', err)
		}
	}

	/**
	 * Fetch the 850 hPa theta-e grid used for front detection. Missing or
	 * failed fetches are tolerated (fronts simply don't render), since the
	 * grid ships separately from the synoptic pressure grid.
	 */
	async ensureFrontsGrid(): Promise<void> {
		const now = Date.now()
		// Fresh by timestamp even when the buffer was released after compute
		if ((now - this.frontsGridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loadingFronts) {
			await this.loadingFronts
			return
		}

		this.loadingFronts = this._loadFrontsGrid()
		try {
			await this.loadingFronts
		} finally {
			this.loadingFronts = null
		}
	}

	private async _loadFrontsGrid(): Promise<void> {
		try {
			const response = await fetch(datasetUrl(frontsGridUrl()))
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch fronts grid: ${response.status}`)
				// Don't hammer the CDN when the grid isn't published yet
				this.frontsGridTimestamp = Date.now()
				return
			}

			recordCrumb("grid:fronts decompress")
			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.frontsGrid = { buffer: decompressed, header }
			this.frontsGridTimestamp = Date.now()

			recordCrumb(`grid:fronts loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Fronts grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[WeatherGrid] Error loading fronts grid:', err)
			this.frontsGridTimestamp = Date.now()
		}
	}

	get frontsGridHeader(): EBWXHeader | null {
		return this.frontsGrid?.header ?? null
	}

	get frontsGridBuffer(): ArrayBuffer | null {
		return this.frontsGrid?.buffer ?? null
	}

	/** Release the decompressed fronts buffer once its consumer has finished */
	releaseFrontsBuffer(): void {
		this.frontsGrid = null
	}

	/**
	 * Fetch the 250 hPa u/v wind grid behind the jet stream shell. Missing or
	 * failed fetches are tolerated (the jet stream simply doesn't render).
	 */
	async ensureJetGrid(): Promise<void> {
		const now = Date.now()
		if ((now - this.jetGridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loadingJet) {
			await this.loadingJet
			return
		}

		this.loadingJet = this._loadJetGrid()
		try {
			await this.loadingJet
		} finally {
			this.loadingJet = null
		}
	}

	private async _loadJetGrid(): Promise<void> {
		try {
			const response = await fetch(datasetUrl(jetGridUrl()))
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch jet grid: ${response.status}`)
				this.jetGridTimestamp = Date.now()
				return
			}

			recordCrumb("grid:jet decompress")
			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.jetGrid = { buffer: decompressed, header }
			this.jetGridTimestamp = Date.now()

			recordCrumb(`grid:jet loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Jet grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[WeatherGrid] Error loading jet grid:', err)
			this.jetGridTimestamp = Date.now()
		}
	}

	get jetGridHeader(): EBWXHeader | null {
		return this.jetGrid?.header ?? null
	}

	get jetGridBuffer(): ArrayBuffer | null {
		return this.jetGrid?.buffer ?? null
	}

	/** Release the decompressed jet buffer once its consumer has finished */
	releaseJetBuffer(): void {
		this.jetGrid = null
	}

	/**
	 * Fetch the 500 hPa geopotential height grid behind the height-contour
	 * shell. Missing or failed fetches are tolerated (the shell stays empty).
	 */
	async ensureUpperGrid(): Promise<void> {
		const now = Date.now()
		if ((now - this.upperGridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loadingUpper) {
			await this.loadingUpper
			return
		}

		this.loadingUpper = this._loadUpperGrid()
		try {
			await this.loadingUpper
		} finally {
			this.loadingUpper = null
		}
	}

	private async _loadUpperGrid(): Promise<void> {
		try {
			const response = await fetch(datasetUrl(upperGridUrl()))
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch upper grid: ${response.status}`)
				this.upperGridTimestamp = Date.now()
				return
			}

			recordCrumb("grid:upper decompress")
			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.upperGrid = { buffer: decompressed, header }
			this.upperGridTimestamp = Date.now()

			recordCrumb(`grid:upper loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Upper grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[WeatherGrid] Error loading upper grid:', err)
			this.upperGridTimestamp = Date.now()
		}
	}

	get upperGridHeader(): EBWXHeader | null {
		return this.upperGrid?.header ?? null
	}

	get upperGridBuffer(): ArrayBuffer | null {
		return this.upperGrid?.buffer ?? null
	}

	/** Release the decompressed upper buffer once its consumer has finished */
	releaseUpperBuffer(): void {
		this.upperGrid = null
	}

	private shardKey(lat: number, lon: number): string {
		const south = Math.floor(lat / SHARD_SIZE_DEG) * SHARD_SIZE_DEG
		const west = Math.floor(lon / SHARD_SIZE_DEG) * SHARD_SIZE_DEG
		return `${formatCoord(south, 'NS')}_${formatCoord(west, 'EW')}`
	}

	private shardUrl(key: string): string {
		return `${GRID_BASE_URL}/forecast/grid_${key}.bin.gz`
	}

	async ensureForecastShard(lat: number, lon: number): Promise<void> {
		const key = this.shardKey(lat, lon)
		const cached = this.forecastCache.get(key)
		if (cached) {
			// LRU touch: re-insert so map order reflects recency
			this.forecastCache.delete(key)
			this.forecastCache.set(key, cached)
			return
		}

		if (this.loadingShards.has(key)) {
			await this.loadingShards.get(key)
			return
		}

		const promise = this._loadShard(key)
		this.loadingShards.set(key, promise)
		try {
			await promise
		} finally {
			this.loadingShards.delete(key)
		}
	}

	private async _loadShard(key: string): Promise<void> {
		try {
			const url = this.shardUrl(key)
			const response = await fetch(datasetUrl(url))
			if (!response.ok) {
				console.warn(`[WeatherGrid] Failed to fetch shard ${key}: ${response.status}`)
				return
			}

			const compressed = await response.arrayBuffer()
			const decompressed = await decompressGzip(compressed)
			const header = decodeEBWXHeader(decompressed)

			this.forecastCache.set(key, { buffer: decompressed, header })
			// Evict least-recently-used shards beyond the cap
			while (this.forecastCache.size > MAX_FORECAST_SHARDS) {
				const oldest = this.forecastCache.keys().next().value
				if (oldest === undefined) break
				this.forecastCache.delete(oldest)
			}
			recordCrumb(`grid:shard ${key} loaded ${mb(decompressed)}MB`)
			console.log(`[WeatherGrid] Shard ${key} loaded: ${header.nTimesteps} timesteps`)
		} catch (err) {
			console.error(`[WeatherGrid] Error loading shard ${key}:`, err)
		}
	}

	/**
	 * Conditions at a point, sampled at `atSec` (unix seconds, defaults to
	 * the real present). Pass the scene clock so displays follow the time
	 * slider; times outside the grid's range clamp to its first/last timestep.
	 */
	async getCurrentWeather(lat: number, lon: number, atSec?: number): Promise<CurrentWeather | null> {
		await this.ensureCurrentGrid()
		if (!this.currentGrid) return null

		const { buffer, header } = this.currentGrid
		const timesteps = decodeEBWXAtPoint(buffer, header, lat, lon)
		return computeCurrentWeather(timesteps, atSec)
	}

	async getDailyForecast(lat: number, lon: number, days: number = 7): Promise<DailyForecast[]> {
		await this.ensureForecastShard(lat, lon)

		const key = this.shardKey(lat, lon)
		const shard = this.forecastCache.get(key)
		if (!shard) return []

		const timesteps = decodeEBWXAtPoint(shard.buffer, shard.header, lat, lon)
		return computeDailyForecasts(timesteps, days)
	}

	async getHourlyForecast(lat: number, lon: number, hours: number = 48): Promise<GridTimestepValues[]> {
		await this.ensureForecastShard(lat, lon)

		const key = this.shardKey(lat, lon)
		const shard = this.forecastCache.get(key)
		if (!shard) return []

		const timesteps = decodeEBWXAtPoint(shard.buffer, shard.header, lat, lon)
		const cutoff = Date.now() / 1000 + hours * 3600
		return timesteps.filter(ts => ts.validTime <= cutoff)
	}

	get isCurrentGridLoaded(): boolean {
		return this.currentGrid !== null
	}

	get currentGridHeader(): EBWXHeader | null {
		return this.currentGrid?.header ?? null
	}

	get currentGridBuffer(): ArrayBuffer | null {
		return this.currentGrid?.buffer ?? null
	}

	getCurrentPressurePlane(timestepIndex: number): EBWXPlane | null {
		if (!this.currentGrid) return null
		return decodeEBWXPlane(
			this.currentGrid.buffer,
			this.currentGrid.header,
			'pressure_hpa',
			timestepIndex
		)
	}

	get isSynopticGridLoaded(): boolean {
		return this.synopticGrid !== null
	}

	get synopticGridHeader(): EBWXHeader | null {
		return this.synopticGrid?.header ?? null
	}

	get synopticGridBuffer(): ArrayBuffer | null {
		return this.synopticGrid?.buffer ?? null
	}

	/** Timestamp of the last successful synoptic grid fetch, for change detection */
	get synopticGridLoadedTimestamp(): number {
		return this.synopticGridTimestamp
	}

	/**
	 * Release the decompressed synoptic buffer (~70MB) once its consumer has
	 * finished computing from it. The load timestamp is kept, so the grid
	 * still counts as fresh and won't be refetched until the refresh
	 * interval elapses.
	 */
	releaseSynopticBuffer(): void {
		this.synopticGrid = null
	}

	getSynopticPressurePlane(timestepIndex: number): EBWXPlane | null {
		if (!this.synopticGrid) return null
		return decodeEBWXPlane(
			this.synopticGrid.buffer,
			this.synopticGrid.header,
			'pressure_hpa',
			timestepIndex
		)
	}

	clearCache(): void {
		this.forecastCache.clear()
		this.currentGrid = null
		this.currentGridTimestamp = 0
		this.synopticGrid = null
		this.synopticGridTimestamp = 0
		this.frontsGrid = null
		this.frontsGridTimestamp = 0
		this.jetGrid = null
		this.jetGridTimestamp = 0
		this.upperGrid = null
		this.upperGridTimestamp = 0
	}
}

function mb(buffer: ArrayBuffer): string {
	return (buffer.byteLength / 1048576).toFixed(1)
}

function formatCoord(value: number, directions: string): string {
	const absVal = Math.abs(Math.floor(value))
	const direction = value >= 0 ? directions[0] : directions[1]
	return `${direction}${String(absVal).padStart(3, '0')}`
}

async function decompressGzip(compressed: ArrayBuffer): Promise<ArrayBuffer> {
	const ds = new DecompressionStream('gzip')
	const writer = ds.writable.getWriter()

	// don't await here: writes can block on backpressure until the reader drains
	const writeDone = (async () => {
		await writer.write(new Uint8Array(compressed))
		await writer.close()
	})()

	const reader = ds.readable.getReader()
	const chunks: Uint8Array[] = []
	let totalLength = 0

	while (true) {
		const { done, value } = await reader.read()
		if (done) break
		chunks.push(value)
		totalLength += value.length
	}

	await writeDone

	const result = new Uint8Array(totalLength)
	let offset = 0
	for (const chunk of chunks) {
		result.set(chunk, offset)
		offset += chunk.length
	}

	return result.buffer
}

let _globalInstance: WeatherGridService | null = null

export function getWeatherGridService(): WeatherGridService {
	if (!_globalInstance) {
		_globalInstance = new WeatherGridService()
	}
	return _globalInstance
}
